import { useEffect, useState } from 'react';
import ReactDatePicker from 'react-date-picker';

import cx from 'classix';
import { TimeValue } from 'react-aria';
import 'react-date-picker/dist/DatePicker.css';
import { HelpIcon } from '../molecules';
import './DatePicker.css';
import { TimePicker } from './TimePicker';

// Converts "January", "February", etc to "Jan", "Feb", etc
const formatMonth = (locale: string | undefined, date: Date) => {
  return date.toLocaleString(locale, { month: 'short' });
};

type Props = {
  initialDate?: Date;
  onChange: (newDate: Date) => void;
  additionalClassNames?: string;
  label?: string;
  includeTime?: boolean;
  minDate?: Date;
  maxDate?: Date;
  helpTextHeader?: string;
  helpTextBody?: string;
  required?: boolean;
};

export const DatePicker = ({
  initialDate,
  onChange,
  label,
  includeTime,
  minDate,
  maxDate,
  additionalClassNames,
  helpTextHeader,
  helpTextBody,
  required,
}: Props) => {
  const [hours = 12, setHours] = useState(initialDate?.getHours());
  const [minutes = 0, setMinutes] = useState(initialDate?.getMinutes());
  const [date, setDate] = useState(initialDate);

  // When used in rh forms initialDate passed as undefined in initial render?
  useEffect(() => {
    setDate(initialDate);
  }, [initialDate]);

  const updateDate = (dateValue: Date) => {
    const newDate = new Date(dateValue);
    if (includeTime) {
      newDate.setUTCHours(hours);
      newDate.setUTCMinutes(minutes);
    }
    setDate(newDate);
    onChange(newDate);
  };

  const updateTime = (timeValue: TimeValue) => {
    const newDate = date ? new Date(date) : new Date();
    const newHours = timeValue.hour;
    const newMinutes = timeValue.minute;
    newDate.setUTCHours(newHours);
    newDate.setUTCMinutes(newMinutes);
    setHours(newHours);
    setMinutes(newMinutes);
    setDate(newDate);
    onChange(newDate);
  };

  const hasHelpText = helpTextHeader || helpTextBody;

  return (
    <div className={`mw-datepicker`}>
      {label && (
        <div className={'text-sm text-gray-500 flex flex-row justify-between'}>{label}</div>
      )}
      <div className='flex flex-row gap-2 '>
        <ReactDatePicker
          onChange={(e) => updateDate(new Date(e as Date))}
          value={date}
          clearIcon={null}
          calendarIcon={null}
          formatMonth={formatMonth}
          minDate={minDate}
          maxDate={maxDate}
          className={cx(additionalClassNames, hasHelpText && 'has-help-text text-gray-700')}
          required={required}
        />
        {hasHelpText && (
          // This wrapping div creates padding to houes the help icon which appears above the input
          <div className='flex ml-[-40px] z-10'>
            <HelpIcon header={helpTextHeader} supportingText={helpTextBody} />
          </div>
        )}
        {includeTime && <TimePicker hours={hours} minutes={minutes} onChange={updateTime} />}
      </div>
    </div>
  );
};
